import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  Button,
  Alert,
  CircularProgress,
  TablePagination,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Lock, LockOpen, Email, Refresh, Settings as SettingsIcon, Save, CheckCircle, Error, HourglassEmpty, Loop, Delete } from '@mui/icons-material';
import api from '../../services/authService';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminConsole = () => {
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [conversionStats, setConversionStats] = useState([]);
  const [stats, setStats] = useState({ users: {}, subscriptions: {}, conversions: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalConversions, setTotalConversions] = useState(0);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  const [orgApiKey, setOrgApiKey] = useState('');
  const [orgHfToken, setOrgHfToken] = useState('');
  const [savingApiKeys, setSavingApiKeys] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [canReprocessFailedMeetings, setCanReprocessFailedMeetings] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [conversionStatusFilter, setConversionStatusFilter] = useState('all');
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [showDeleteOrgDialog, setShowDeleteOrgDialog] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [deletingOrg, setDeletingOrg] = useState(false);

  useEffect(() => {
    fetchStats();
    if (tabValue === 0) {
      fetchUsers();
    } else if (tabValue === 1) {
      fetchOrganizations();
    } else if (tabValue === 2) {
      fetchConversions();
      fetchConversionStatsBySubscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, page, rowsPerPage, searchTerm, conversionStatusFilter]);

  const fetchStats = async () => {
    try {
      const [userStats, subStats, convStats] = await Promise.all([
        api.get('/admin/stats/users'),
        api.get('/admin/stats/subscriptions'),
        api.get('/admin/stats/conversions')
      ]);
      setStats({
        users: userStats.data,
        subscriptions: subStats.data,
        conversions: convStats.data
      });
    } catch (err) {
      console.error('Failed to load stats');
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/users', {
        params: {
          page: page + 1,
          perPage: rowsPerPage,
          search: searchTerm
        }
      });
      setUsers(response.data.users);
      setTotalUsers(response.data.total);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/organizations', {
        params: {
          page: page + 1,
          perPage: rowsPerPage
        }
      });
      setOrganizations(response.data.organizations);
    } catch (err) {
      setError('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const fetchConversions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/conversions', {
        params: {
          page: page + 1,
          perPage: rowsPerPage,
          status: conversionStatusFilter === 'all' ? undefined : conversionStatusFilter
        }
      });
      setConversions(response.data.conversions);
      setTotalConversions(response.data.total);
    } catch (err) {
      setError('Failed to load conversions');
    } finally {
      setLoading(false);
    }
  };

  const fetchConversionStatsBySubscription = async () => {
    try {
      const response = await api.get('/admin/stats/conversions-by-subscription');
      setConversionStats(response.data.stats);
    } catch (err) {
      console.error('Failed to load conversion stats by subscription');
    }
  };

  const handleToggleUserLock = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/toggle-lock`);
      setSuccess('User status updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchUsers();
    } catch (err) {
      setError('Failed to update user status');
    }
  };

  const handleResendVerification = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/resend-verification`);
      setSuccess('Verification email sent successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to send verification email');
    }
  };

  const handleToggleOrgLock = async (orgId) => {
    try {
      await api.post(`/admin/organizations/${orgId}/toggle-lock`);
      setSuccess('Organization status updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchOrganizations();
    } catch (err) {
      setError('Failed to update organization status');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenApiKeyDialog = async (org) => {
    setSelectedOrg(org);
    setShowApiKeyDialog(true);
    try {
      const response = await api.get(`/admin/organizations/${org.id}/api-keys`);
      setOrgApiKey(response.data.api_key || '');
      setOrgHfToken(response.data.hf_token || '');
    } catch (err) {
      setError('Failed to load organization API keys');
    }
  };

  const handleSaveOrgApiKeys = async () => {
    if (!selectedOrg) return;
    
    try {
      setSavingApiKeys(true);
      await api.put(`/admin/organizations/${selectedOrg.id}/api-keys`, {
        api_key: orgApiKey,
        hf_token: orgHfToken
      });
      setSuccess('Organization API keys updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      setShowApiKeyDialog(false);
    } catch (err) {
      setError('Failed to update organization API keys');
    } finally {
      setSavingApiKeys(false);
    }
  };

  const handleCloseApiKeyDialog = () => {
    setShowApiKeyDialog(false);
    setSelectedOrg(null);
    setOrgApiKey('');
    setOrgHfToken('');
  };

  const handleOpenPermissionsDialog = (user) => {
    setSelectedUser(user);
    setCanReprocessFailedMeetings(user.can_reprocess_failed_meetings || false);
    setShowPermissionsDialog(true);
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;
    
    try {
      setSavingPermissions(true);
      await api.put(`/admin/users/${selectedUser.id}/permissions`, {
        can_reprocess_failed_meetings: canReprocessFailedMeetings
      });
      setSuccess('User permissions updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      setShowPermissionsDialog(false);
      fetchUsers();
    } catch (err) {
      setError('Failed to update user permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleClosePermissionsDialog = () => {
    setShowPermissionsDialog(false);
    setSelectedUser(null);
    setCanReprocessFailedMeetings(false);
  };

  const handleOpenDeleteUserDialog = (user) => {
    setUserToDelete(user);
    setShowDeleteUserDialog(true);
  };

  const handleCloseDeleteUserDialog = () => {
    setShowDeleteUserDialog(false);
    setUserToDelete(null);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setDeletingUser(true);
      await api.delete(`/admin/users/${userToDelete.id}`);
      setSuccess(`User ${userToDelete.email} deleted successfully`);
      setTimeout(() => setSuccess(''), 3000);
      setShowDeleteUserDialog(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setDeletingUser(false);
    }
  };

  const handleOpenDeleteOrgDialog = (org) => {
    setOrgToDelete(org);
    setShowDeleteOrgDialog(true);
  };

  const handleCloseDeleteOrgDialog = () => {
    setShowDeleteOrgDialog(false);
    setOrgToDelete(null);
  };

  const handleDeleteOrg = async () => {
    if (!orgToDelete) return;
    
    try {
      setDeletingOrg(true);
      await api.delete(`/admin/organizations/${orgToDelete.id}`);
      setSuccess(`Organization ${orgToDelete.name} deleted successfully`);
      setTimeout(() => setSuccess(''), 3000);
      setShowDeleteOrgDialog(false);
      setOrgToDelete(null);
      fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete organization');
    } finally {
      setDeletingOrg(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h3" gutterBottom>
          Admin Console
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Stats Dashboard */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Users
                </Typography>
                <Typography variant="h4">
                  {stats.users.total || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Active Users
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.users.active || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Active Subscriptions
                </Typography>
                <Typography variant="h4" color="primary.main">
                  {stats.subscriptions.active || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pending Verification
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.users.pendingVerification || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Conversions
                </Typography>
                <Typography variant="h4">
                  {stats.conversions.total || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pending Conversions
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.conversions.pending || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Completed Conversions
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.conversions.completed || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Failed Conversions
                </Typography>
                <Typography variant="h4" color="error.main">
                  {stats.conversions.failed || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Users" />
            <Tab label="Organizations" />
            <Tab label="Conversions" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
              <TextField
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                fullWidth
              />
              <Button startIcon={<Refresh />} onClick={fetchUsers}>
                Refresh
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Organization</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Trial</TableCell>
                        <TableCell>Subscription</TableCell>
                        <TableCell>Joined</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            {user.firstName} {user.lastName}
                          </TableCell>
                          <TableCell>
                            {user.organization?.name || 'None'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.accountStatus}
                              color={
                                user.accountStatus === 'active'
                                  ? 'success'
                                  : user.accountStatus === 'disabled'
                                  ? 'error'
                                  : 'warning'
                              }
                              size="small"
                            />
                            {!user.emailVerified && (
                              <Chip label="Unverified" color="warning" size="small" sx={{ ml: 1 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            {user.trial?.trialActive ? (
                              <Chip
                                label={`${Math.floor(user.trial.remainingMinutes)} min left`}
                                color="info"
                                size="small"
                              />
                            ) : user.trial?.trialStartedAt ? (
                              <Chip label="Expired" size="small" />
                            ) : (
                              <Chip label="Not started" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            {user.subscription ? (
                              <Chip
                                label={user.subscription.status}
                                color={user.subscription.status === 'active' ? 'success' : 'default'}
                                size="small"
                              />
                            ) : (
                              <Chip label="None" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            {new Date(user.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleToggleUserLock(user.id)}
                              color={user.accountStatus === 'disabled' ? 'success' : 'error'}
                            >
                              {user.accountStatus === 'disabled' ? <LockOpen /> : <Lock />}
                            </IconButton>
                            {!user.emailVerified && (
                              <IconButton
                                size="small"
                                onClick={() => handleResendVerification(user.id)}
                                color="primary"
                              >
                                <Email />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => handleOpenPermissionsDialog(user)}
                              color="primary"
                              title="Manage Permissions"
                            >
                              <SettingsIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteUserDialog(user)}
                              color="error"
                              title="Delete User"
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={totalUsers}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button startIcon={<Refresh />} onClick={fetchOrganizations}>
                Refresh
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Members</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {organizations.map((org) => (
                      <TableRow key={org.id}>
                        <TableCell>
                          <Typography variant="body1" fontWeight="bold">
                            {org.name}
                          </Typography>
                          {org.description && (
                            <Typography variant="caption" color="textSecondary">
                              {org.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{org.users?.length || 0}</TableCell>
                        <TableCell>
                          <Chip
                            label={org.isActive ? 'Active' : 'Inactive'}
                            color={org.isActive ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(org.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenApiKeyDialog(org)}
                            color="primary"
                            title="Configure API Keys"
                          >
                            <SettingsIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleOrgLock(org.id)}
                            color={org.isActive ? 'error' : 'success'}
                          >
                            {org.isActive ? <Lock /> : <LockOpen />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDeleteOrgDialog(org)}
                            color="error"
                            title="Delete Organization"
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                select
                label="Status Filter"
                value={conversionStatusFilter}
                onChange={(e) => {
                  setConversionStatusFilter(e.target.value);
                  setPage(0);
                }}
                size="small"
                sx={{ minWidth: 150 }}
                SelectProps={{ native: true }}
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </TextField>
              <Button startIcon={<Refresh />} onClick={fetchConversions}>
                Refresh
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell>Recording</TableCell>
                        <TableCell>Meeting</TableCell>
                        <TableCell>Organization</TableCell>
                        <TableCell>Format</TableCell>
                        <TableCell>Attempts</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Completed</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {conversions.map((conversion) => (
                        <TableRow key={conversion.id}>
                          <TableCell>
                            <Chip
                              icon={
                                conversion.status === 'completed' ? <CheckCircle /> :
                                conversion.status === 'processing' ? <Loop /> :
                                conversion.status === 'failed' ? <Error /> :
                                <HourglassEmpty />
                              }
                              label={conversion.status}
                              color={
                                conversion.status === 'completed' ? 'success' :
                                conversion.status === 'processing' ? 'info' :
                                conversion.status === 'failed' ? 'error' :
                                'warning'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {conversion.meetingRecording?.filename || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {conversion.meetingRecording?.hostedMeeting?.title || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {conversion.organization?.name || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {conversion.sourceFormat} → {conversion.targetFormat}
                          </TableCell>
                          <TableCell>
                            {conversion.attempts} / {conversion.maxAttempts}
                          </TableCell>
                          <TableCell>
                            {new Date(conversion.createdAt).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {conversion.processingCompletedAt 
                              ? new Date(conversion.processingCompletedAt).toLocaleString()
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={totalConversions}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />

                {/* Per-Subscription Statistics */}
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h5" gutterBottom>
                    Conversions by Organization
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Organization</TableCell>
                          <TableCell>Subscription Status</TableCell>
                          <TableCell>Plan(s)</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="right">Pending</TableCell>
                          <TableCell align="right">Processing</TableCell>
                          <TableCell align="right">Completed</TableCell>
                          <TableCell align="right">Failed</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {conversionStats.map((stat) => (
                          <TableRow key={stat.organizationId}>
                            <TableCell>{stat.organizationName}</TableCell>
                            <TableCell>
                              <Chip
                                label={stat.hasActiveSubscription ? 'Active' : 'No Subscription'}
                                color={stat.hasActiveSubscription ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {stat.planIds.length > 0 ? stat.planIds.join(', ') : '-'}
                            </TableCell>
                            <TableCell align="right">{stat.conversions.total}</TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={stat.conversions.pending} 
                                color="warning" 
                                size="small"
                                sx={{ minWidth: 40 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={stat.conversions.processing} 
                                color="info" 
                                size="small"
                                sx={{ minWidth: 40 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={stat.conversions.completed} 
                                color="success" 
                                size="small"
                                sx={{ minWidth: 40 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={stat.conversions.failed} 
                                color="error" 
                                size="small"
                                sx={{ minWidth: 40 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </>
            )}
          </TabPanel>
        </Paper>

        {/* API Key Configuration Dialog */}
        <Dialog 
          open={showApiKeyDialog} 
          onClose={handleCloseApiKeyDialog}
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>
            Configure API Keys
            {selectedOrg && (
              <Typography variant="body2" color="textSecondary">
                {selectedOrg.name}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="OpenAI API Key"
              type="password"
              value={orgApiKey}
              onChange={(e) => setOrgApiKey(e.target.value)}
              placeholder="sk-..."
              helperText="OpenAI API key for transcription processing"
              margin="normal"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="HuggingFace Token"
              type="password"
              value={orgHfToken}
              onChange={(e) => setOrgHfToken(e.target.value)}
              placeholder="hf_..."
              helperText="HuggingFace token for model access"
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseApiKeyDialog}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={savingApiKeys ? <CircularProgress size={20} color="inherit" /> : <Save />}
              onClick={handleSaveOrgApiKeys}
              disabled={savingApiKeys}
            >
              {savingApiKeys ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* User Permissions Dialog */}
        <Dialog 
          open={showPermissionsDialog} 
          onClose={handleClosePermissionsDialog}
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>
            Manage User Permissions
            {selectedUser && (
              <Typography variant="body2" color="textSecondary">
                {selectedUser.firstName} {selectedUser.lastName} ({selectedUser.email})
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={canReprocessFailedMeetings}
                    onChange={(e) => setCanReprocessFailedMeetings(e.target.checked)}
                  />
                }
                label="Can Reprocess Failed Meetings"
              />
              <Typography variant="caption" display="block" color="textSecondary" sx={{ ml: 4 }}>
                Allows user to trigger reprocessing of failed meeting transcriptions
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePermissionsDialog}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={savingPermissions ? <CircularProgress size={20} color="inherit" /> : <Save />}
              onClick={handleSavePermissions}
              disabled={savingPermissions}
            >
              {savingPermissions ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete User Confirmation Dialog */}
        <Dialog 
          open={showDeleteUserDialog} 
          onClose={handleCloseDeleteUserDialog}
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>
            Delete User
          </DialogTitle>
          <DialogContent>
            {userToDelete && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Warning: This action cannot be undone!
                  </Typography>
                </Alert>
                <Typography variant="body1" gutterBottom>
                  Are you sure you want to delete this user?
                </Typography>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  <strong>Email:</strong> {userToDelete.email}
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {userToDelete.firstName} {userToDelete.lastName}
                </Typography>
                {userToDelete.organization && (
                  <Typography variant="body2">
                    <strong>Organization:</strong> {userToDelete.organization.name}
                  </Typography>
                )}
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                  This will permanently delete:
                </Typography>
                <Box component="ul" sx={{ mt: 1, color: 'error.main' }}>
                  <li>User account and profile</li>
                  <li>All meetings uploaded by this user</li>
                  <li>All hosted meetings and recordings</li>
                  <li>All scheduled meetings</li>
                  <li>Subscription and trial data</li>
                  <li>All related analytics and logs</li>
                </Box>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  <strong>Note:</strong> The organization and other users' data will remain intact.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteUserDialog} disabled={deletingUser}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={deletingUser ? <CircularProgress size={20} color="inherit" /> : <Delete />}
              onClick={handleDeleteUser}
              disabled={deletingUser}
            >
              {deletingUser ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Organization Confirmation Dialog */}
        <Dialog 
          open={showDeleteOrgDialog} 
          onClose={handleCloseDeleteOrgDialog}
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>
            Delete Organization
          </DialogTitle>
          <DialogContent>
            {orgToDelete && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    CRITICAL WARNING: This action cannot be undone!
                  </Typography>
                </Alert>
                <Typography variant="body1" gutterBottom>
                  Are you sure you want to delete this organization?
                </Typography>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  <strong>Organization:</strong> {orgToDelete.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Members:</strong> {orgToDelete.users?.length || 0} users
                </Typography>
                <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'bold' }}>
                  This will permanently delete:
                </Typography>
                <Box component="ul" sx={{ mt: 1, color: 'error.main' }}>
                  <li>The organization and all settings</li>
                  <li>ALL users in the organization</li>
                  <li>ALL meetings (uploaded and hosted)</li>
                  <li>ALL recordings and transcripts</li>
                  <li>ALL scheduled meetings</li>
                  <li>ALL subscriptions</li>
                  <li>ALL analytics and logs</li>
                  <li>ALL conversion queue items</li>
                </Box>
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    This will affect <strong>{orgToDelete.users?.length || 0} users</strong> and remove all their data permanently.
                  </Typography>
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteOrgDialog} disabled={deletingOrg}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={deletingOrg ? <CircularProgress size={20} color="inherit" /> : <Delete />}
              onClick={handleDeleteOrg}
              disabled={deletingOrg}
            >
              {deletingOrg ? 'Deleting...' : 'Delete Organization'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default AdminConsole;
